import { useEffect, useRef, useState } from "react";
import { AutoComplete, AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import toast from "react-hot-toast";

import { AppDialog, FormField, FormRow } from "../../../components/ui";
import {
  CreatedPointAdjustment,
  MAX_POINT_ADJUSTMENT_INTERNAL_REASON,
  MAX_POINT_ADJUSTMENT_POINTS,
  MAX_POINT_ADJUSTMENT_USER_REASON,
  MIN_POINT_ADJUSTMENT_SEARCH,
  PointAdjustmentUserOption,
} from "../../../models/pointLedger";
import {
  useCreatePointAdjustmentMutation,
  useLazySearchPointAdjustmentUsersQuery,
} from "../../../store/services/point-ledger";
import { getApiErrorMessage } from "../../../utils/functions/api-errors";
import {
  PointAdjustmentDirection,
  PointAdjustmentFormErrors,
  PointAdjustmentFormValues,
  buildCreatePointAdjustmentPayload,
  buildInitialPointAdjustmentValues,
  formatAdjustmentSuccess,
  validatePointAdjustmentForm,
} from "./pointAdjustmentForm";

export interface PointAdjustmentDialogProps {
  visible: boolean;
  onHide: () => void;
  /** `null` = SUPER_ADMIN en "Todas": tiene que elegir una en el formulario. */
  tenantId: string | null;
  tenantOptions: { label: string; value: string }[];
}

const DIRECTION_OPTIONS: { label: string; value: PointAdjustmentDirection }[] = [
  { label: "Sumar", value: "add" },
  { label: "Restar", value: "subtract" },
];

const SAVE_FALLBACK_MESSAGE = "No se pudo cargar el movimiento";

const USER_REASON_HINT =
  "Esto es LO QUE LA PERSONA VA A LEER en su historial de puntos, en lugar de \"Ajuste de puntos\".";
const INTERNAL_REASON_HINT =
  "No se le muestra a la persona. Queda en el movimiento y en el registro de auditoría.";
const SUBTRACT_HINT =
  "Si el descuento supera el saldo, el movimiento se rechaza: no se descuenta lo que haya.";
const EMAIL_HINT = "Escribí parte del mail y elegí a la persona de la lista.";
const NO_TENANT_HINT = "Elegí primero una institución para poder buscar.";
const SEARCH_DEBOUNCE_MS = 300;

const isUserOption = (value: unknown): value is PointAdjustmentUserOption =>
  typeof value === "object" && value !== null && "email" in value;

/**
 * Carga de un movimiento manual de puntos.
 *
 * EL SIGNO SE ELIGE CON DOS BOTONES y el monto se escribe siempre positivo. Un solo campo con
 * signo deja pasar el error más caro de esta pantalla —un menos de más o de menos— sin que
 * nada en la UI lo delate; con dos botones, lo que se eligió se lee de un vistazo y el
 * resumen de confirmación lo repite en palabras.
 *
 * LA PERSONA SE BUSCA, NO SE ADIVINA. Antes era una caja de texto donde había que pegar el
 * mail exacto: si el reclamo traía el nombre, o el mail con una letra distinta, el formulario
 * no ayudaba en nada y el error recién aparecía al confirmar. El buscador consulta sólo el
 * padrón de la institución elegida y muestra nombre, mail y SALDO de cada opción, que es lo
 * que hace falta para decidir un descuento.
 *
 * SE PUEDE SEGUIR ESCRIBIENDO UN MAIL A MANO (no hay `forceSelection`): pegar el mail que vino
 * en el reclamo sigue siendo el camino más corto cuando se lo tiene completo, y el backend
 * valida igual que exista y que pertenezca a la institución.
 */
export const PointAdjustmentDialog = ({
  visible,
  onHide,
  tenantId,
  tenantOptions,
}: PointAdjustmentDialogProps) => {
  const [values, setValues] = useState<PointAdjustmentFormValues>(() =>
    buildInitialPointAdjustmentValues(tenantId)
  );
  const [attempted, setAttempted] = useState(false);
  /** Lo que muestra el buscador: la opción elegida, o el texto crudo mientras se escribe. */
  const [emailInput, setEmailInput] = useState<PointAdjustmentUserOption | string>("");
  const [userOptions, setUserOptions] = useState<PointAdjustmentUserOption[]>([]);
  /**
   * Las respuestas de un typeahead vuelven desordenadas: sin esto, la búsqueda de "an" que
   * tardó más pisa los resultados de "ana.p" y el desplegable muestra lo que ya no se buscó.
   */
  const searchVersion = useRef(0);
  const [searchUsers] = useLazySearchPointAdjustmentUsersQuery();
  const [createPointAdjustment, { isLoading }] = useCreatePointAdjustmentMutation();

  // Cada apertura arranca en blanco: un formulario que mueve saldos ajenos no puede quedar
  // con el mail de la persona anterior cargado.
  useEffect(() => {
    if (!visible) return;
    searchVersion.current += 1;
    setValues(buildInitialPointAdjustmentValues(tenantId));
    setAttempted(false);
    setEmailInput("");
    setUserOptions([]);
  }, [visible, tenantId]);

  const setField = <K extends keyof PointAdjustmentFormValues>(
    field: K,
    value: PointAdjustmentFormValues[K]
  ) => setValues((previous) => ({ ...previous, [field]: value }));

  /**
   * EL BUSCADOR ESTÁ ATADO A LA INSTITUCIÓN ELEGIDA, no al scope del admin: un SUPER_ADMIN en
   * "Todas" primero elige institución y recién ahí hay un padrón donde buscar. Sin tenant el
   * endpoint responde 403, así que ni se consulta.
   */
  const searchTenantId: string | null = values.tenantId;

  const completeUserSearch = async ({ query }: AutoCompleteCompleteEvent) => {
    const search: string = query.trim();
    const version: number = (searchVersion.current += 1);

    if (searchTenantId === null || search.length < MIN_POINT_ADJUSTMENT_SEARCH) {
      setUserOptions([]);
      return;
    }

    try {
      const found = await searchUsers({ search, tenantId: searchTenantId }, true).unwrap();
      if (version !== searchVersion.current) return;
      setUserOptions(found);
    } catch {
      // Un buscador que falla no puede bloquear el alta: se puede escribir el mail completo y
      // el backend valida igual.
      if (version === searchVersion.current) setUserOptions([]);
    }
  };

  const handleEmailChange = (value: PointAdjustmentUserOption | string) => {
    if (isUserOption(value)) {
      searchVersion.current += 1;
      setEmailInput(value);
      setField("email", value.email);
      return;
    }
    setEmailInput(value);
    setField("email", value);
  };

  const errors = validatePointAdjustmentForm(values);
  const shownErrors: PointAdjustmentFormErrors = attempted ? errors : {};

  const handleSave = async () => {
    setAttempted(true);

    const payload = buildCreatePointAdjustmentPayload(values);
    if (payload === null) return;

    try {
      const created: CreatedPointAdjustment = await createPointAdjustment(payload).unwrap();
      toast.success(
        formatAdjustmentSuccess(created.points, created.user.fullName, created.balanceAfter)
      );
      onHide();
    } catch (error) {
      // El saldo insuficiente viaja con el saldo adentro del mensaje del backend, así que
      // alcanza con mostrarlo: el admin ve cuánto hay y decide.
      toast.error(getApiErrorMessage(error) ?? SAVE_FALLBACK_MESSAGE);
    }
  };

  return (
    <AppDialog
      variant="form"
      visible={visible}
      onHide={onHide}
      header="Nuevo movimiento de puntos"
      primaryLabel="Cargar movimiento"
      onPrimary={handleSave}
      loading={isLoading}
      className="neutral-input-controls"
      style={{ width: "min(92vw, 560px)" }}
    >
      {tenantId === null && (
        <FormRow>
          <FormField
            label="Institución"
            htmlFor="pointAdjustmentTenant"
            required
            error={shownErrors.tenantId}
          >
            <Dropdown
              inputId="pointAdjustmentTenant"
              value={values.tenantId}
              options={tenantOptions}
              onChange={(event) => {
                // Cambiar de institución VACÍA la persona elegida: el mail que estaba
                // seleccionado es de un padrón que ya no es el que se está mirando, y el alta
                // lo rechazaría con un 404 que no explica nada.
                searchVersion.current += 1;
                setEmailInput("");
                setUserOptions([]);
                setField("email", "");
                setField("tenantId", event.value as string);
              }}
              placeholder="Elegí una institución"
              filter
              className="w-full"
            />
          </FormField>
        </FormRow>
      )}

      <FormRow>
        <FormField
          label="Mail de la persona"
          htmlFor="pointAdjustmentEmail"
          required
          error={shownErrors.email}
        >
          <AutoComplete
            inputId="pointAdjustmentEmail"
            value={emailInput}
            suggestions={userOptions}
            field="email"
            completeMethod={completeUserSearch}
            minLength={MIN_POINT_ADJUSTMENT_SEARCH}
            delay={SEARCH_DEBOUNCE_MS}
            placeholder="ana.perez@ejemplo.com"
            maxLength={254}
            emptyMessage="No hay nadie con ese mail en la institución"
            inputClassName="w-full"
            className="w-full"
            itemTemplate={(option: PointAdjustmentUserOption) => (
              <div className="flex flex-column">
                <span className="font-medium">{option.email}</span>
                <small className="text-600">
                  {option.fullName} · {option.balance} puntos
                </small>
              </div>
            )}
            onChange={(event) =>
              handleEmailChange(event.value as PointAdjustmentUserOption | string)
            }
          />
          <small className="text-600">
            {searchTenantId === null ? NO_TENANT_HINT : EMAIL_HINT}
          </small>
        </FormField>
      </FormRow>

      <FormRow>
        <FormField label="Operación" htmlFor="pointAdjustmentDirection" required>
          <SelectButton
            id="pointAdjustmentDirection"
            value={values.direction}
            options={DIRECTION_OPTIONS}
            onChange={(event) => {
              // PrimeReact manda `null` cuando se vuelve a tocar el botón ya elegido: sin
              // esto, el formulario quedaría sin operación y el payload sin signo.
              if (event.value === null || event.value === undefined) return;
              setField("direction", event.value as PointAdjustmentDirection);
            }}
            allowEmpty={false}
          />
        </FormField>
        <FormField
          label="Puntos"
          htmlFor="pointAdjustmentPoints"
          required
          error={shownErrors.points}
        >
          <InputNumber
            inputId="pointAdjustmentPoints"
            value={values.points}
            onValueChange={(event) => setField("points", event.value ?? null)}
            min={0}
            max={MAX_POINT_ADJUSTMENT_POINTS}
            useGrouping={false}
            placeholder="0"
            className="w-full"
          />
        </FormField>
      </FormRow>

      {values.direction === "subtract" && (
        <Message severity="warn" className="w-full justify-content-start mb-3" text={SUBTRACT_HINT} />
      )}

      <FormRow>
        <FormField
          label="Motivo interno"
          htmlFor="pointAdjustmentInternalReason"
          required
          error={shownErrors.internalReason}
        >
          <InputTextarea
            id="pointAdjustmentInternalReason"
            value={values.internalReason}
            onChange={(event) => setField("internalReason", event.target.value)}
            rows={2}
            maxLength={MAX_POINT_ADJUSTMENT_INTERNAL_REASON}
            placeholder="Ej: reclamo #482, el host cargó mal la lista del martes."
            className="w-full"
          />
          <small className="text-600">{INTERNAL_REASON_HINT}</small>
        </FormField>
      </FormRow>

      <FormRow>
        <FormField
          label="Motivo para la persona"
          htmlFor="pointAdjustmentUserReason"
          required
          error={shownErrors.userReason}
        >
          <InputTextarea
            id="pointAdjustmentUserReason"
            value={values.userReason}
            onChange={(event) => setField("userReason", event.target.value)}
            rows={2}
            maxLength={MAX_POINT_ADJUSTMENT_USER_REASON}
            placeholder="Ej: te devolvimos los puntos de la clase del martes."
            className="w-full"
          />
          <small className="text-600">{USER_REASON_HINT}</small>
        </FormField>
      </FormRow>
    </AppDialog>
  );
};
